(() => {
  'use strict';

  if (window.drboGuaranteeLoaded) return;
  window.drboGuaranteeLoaded = true;

  const componentName = 'drbo-guarantee';
  const productScope = '.js-product-card, [data-js-product-container], .js-quick-view, [data-section-id], .shopify-section';
  let modal;
  let heading;
  let content;
  let closeButton;
  let opener;
  const families = { '500': '500', '500pro': '500', '500proaccore': '500', b500: '500', bk215plus: 'bk', b215plus: 'bk', ev3600: 'ev' };
  const configuratorStates = new Map();
  let catalogText;
  let catalog;

  function readCatalog() {
    const source = document.getElementById('drbo-guarantee-catalog');
    if (!source) return null;
    if (source.textContent !== catalogText) {
      catalogText = source.textContent;
      try {
        catalog = JSON.parse(catalogText);
      } catch (_) {
        catalog = null;
      }
    }
    return catalog && typeof catalog === 'object' && !Array.isArray(catalog) ? catalog : null;
  }

  function readQuantity(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : 0;
  }

  document.addEventListener('drbo:configurator:update', (event) => {
    const state = event.detail;
    if (!state?.configuratorId || !Array.isArray(state.items)) return;
    configuratorStates.set(state.configuratorId, state);
    document.querySelectorAll('drbo-guarantee[data-guarantee-configurator]').forEach((element) => element.sync());
  });

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function closeDialog() {
    if (modal && modal.open) modal.close();
  }

  function ensureDialog() {
    if (modal) return modal;
    modal = createElement('dialog', 'drbo-guarantee__dialog');
    modal.setAttribute('aria-labelledby', 'drbo-guarantee-dialog-heading');
    const header = createElement('div', 'drbo-guarantee__dialog-header');
    heading = createElement('h2', 'drbo-guarantee__heading');
    heading.id = 'drbo-guarantee-dialog-heading';
    closeButton = createElement('button', 'drbo-guarantee__dialog-close', '\u00d7');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', 'Schlie\u00dfen');
    closeButton.addEventListener('click', closeDialog);
    header.append(heading, closeButton);
    content = createElement('div', 'drbo-guarantee__dialog-content');
    modal.append(header, content);
    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    // Keep the theme's Esc handler from closing the underlying quick-view drawer.
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeDialog();
      }
    });
    modal.addEventListener('focusin', (event) => event.stopPropagation());
    modal.addEventListener('focusout', (event) => event.stopPropagation());
    // Dialog clicks are not outside clicks for the notification underneath it.
    modal.addEventListener('click', (event) => event.stopPropagation());
    modal.addEventListener('close', () => {
      if (modal.open) return;
      const previous = opener;
      opener = null;
      content.replaceChildren();
      if (previous && previous.isConnected && previous.getClientRects().length) {
        previous.focus({ preventScroll: true });
      }
    });
    document.body.append(modal);
    return modal;
  }

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element && event.target.closest('a[data-guarantee-open]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!window.HTMLDialogElement || !HTMLDialogElement.prototype.showModal) return;
    let url;
    try {
      url = new URL(link.href, document.baseURI);
    } catch (_) {
      return;
    }
    if (!['https:', 'http:'].includes(url.protocol)) return;
    const dialog = ensureDialog();
    const title = link.dataset.guaranteeTitle || link.querySelector('img')?.alt || 'Information';
    const template = document.getElementById(link.dataset.guaranteeContentId || '');
    const fragment = document.createDocumentFragment();
    const img = createElement('img', 'drbo-guarantee__dialog-image');
    img.alt = title;
    img.src = url.href;
    const error = createElement('p', 'drbo-guarantee__note', 'Das Bild konnte nicht geladen werden.');
    error.hidden = true;
    error.setAttribute('role', 'status');
    img.addEventListener('error', () => { error.hidden = false; });
    fragment.append(img, error);
    if (template instanceof HTMLTemplateElement && template.hasAttribute('data-guarantee-content')) {
      fragment.append(template.content.cloneNode(true));
    }
    if (link.dataset.guaranteeUrl) {
      try {
        const officialUrl = new URL(link.dataset.guaranteeUrl);
        if (officialUrl.protocol === 'https:') {
          const official = createElement('a', 'drbo-guarantee__documents', 'Informationen der Europ\u00e4ischen Union');
          official.href = officialUrl.href;
          official.target = '_blank';
          official.rel = 'noopener';
          fragment.append(official);
        }
      } catch (_) {
        // Invalid optional URLs must not prevent opening the original document.
      }
    }
    const original = createElement('a', 'drbo-guarantee__documents', 'Original \u00f6ffnen');
    original.href = url.href;
    original.target = '_blank';
    original.rel = 'noopener';
    fragment.append(original);
    heading.textContent = title;
    content.replaceChildren(fragment);
    opener = link;
    try {
      if (!dialog.open) dialog.showModal();
      dialog.scrollTop = 0;
      closeButton.focus({ preventScroll: true });
      event.preventDefault();
    } catch (_) {
      opener = null;
      // A normal link remains the fallback if the browser cannot open a dialog.
    }
  });

  class DrboGuarantee extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest(productScope) || this.parentElement;
      this.subscriptions = [];
      this.onReady = () => this.schedule();
      this.onInput = (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.matches('[name="id"]') && event.target.form === this.form) {
          this.eventSelection = undefined;
          this.showVariant(event.target.value);
        } else {
          this.schedule();
        }
      };
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      if (this.scope) {
        this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'form', 'id'] });
        this.scope.addEventListener('change', this.onInput);
        this.scope.addEventListener('input', this.onInput);
      }
      document.addEventListener('theme:all:loaded', this.onReady);
      document.addEventListener('DOMContentLoaded', this.onReady);
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.onInput);
      this.scope?.removeEventListener('input', this.onInput);
      document.removeEventListener('theme:all:loaded', this.onReady);
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.unlisten();
    }

    schedule() {
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (this.isConnected) this.sync();
      });
    }

    findForm() {
      const explicit = document.getElementById(this.dataset.guaranteeFormId || '');
      if (explicit instanceof HTMLFormElement) return explicit;
      const parent = this.closest('form');
      if (parent) return parent;
      if (!this.scope) return null;
      const forms = [...this.scope.querySelectorAll('form')];
      const prefix = `product_form_${this.dataset.productId}_section_`;
      const matched = forms.find((form) => (form.getAttribute('id') || '').startsWith(prefix));
      if (matched) return matched;
      const candidates = forms.filter((form) => form.matches('[data-js-product-form], .js-product-card-form, .js-quick-view-form'));
      return candidates.length === 1 ? candidates[0] : null;
    }

    inputValue() {
      // form.elements includes the theme's inputs that live outside the form.
      const input = this.form && [...this.form.elements].find((element) => element.name === 'id');
      return input ? String(input.value) : undefined;
    }

    unlisten() {
      (this.subscriptions || []).forEach(([bus, name, handler]) => {
        if (bus.all?.()[name]) bus.remove(name, handler);
      });
      this.subscriptions = [];
    }

    bindEvents() {
      const bus = window.themeCore?.EventBus;
      const section = this.closest('[data-section-id]');
      const sectionId = section?.dataset.sectionId;
      const formId = this.form?.getAttribute('id');
      const key = `${formId || ''}|${sectionId || ''}`;
      if (this.bus === bus && this.eventKey === key) return;
      this.unlisten();
      this.bus = bus;
      this.eventKey = key;
      if (!bus?.listen || !bus?.remove) return;
      const listen = (name, handler) => {
        bus.listen(name, handler);
        this.subscriptions.push([bus, name, handler]);
      };
      const select = (id) => {
        this.eventSelection = { id: id == null ? '' : String(id), input: this.inputValue() };
        this.showVariant(this.eventSelection.id);
      };
      if (formId) {
        listen(`form:${formId}:change-variant`, (data) => {
          if (data && (!data.form || data.form === this.form)) select(data.currentVariant?.id);
        });
      }
      if (sectionId && !this.closest('.js-product-card, .js-quick-view-form')) {
        listen(`pdp:section-${sectionId}:change-variant`, (data) => {
          if (!data || (data.product?.id && String(data.product.id) !== this.dataset.productId)) return;
          select(data.variantId ?? data.variant?.id);
        });
      }
      listen('product-card:change-variant', () => this.schedule());
      listen('product:loaded', () => this.schedule());
    }

    sync() {
      if (this.hasAttribute('data-guarantee-configurator')) {
        this.syncConfigurator();
        return;
      }
      const form = this.findForm();
      if (form !== this.form) {
        this.form = form;
        this.eventSelection = undefined;
      }
      this.bindEvents();
      const value = this.inputValue();
      if (this.eventSelection && this.eventSelection.input === value) {
        this.showVariant(this.eventSelection.id);
        return;
      }
      this.eventSelection = undefined;
      // Quick view can leave an old hidden ID when the new option combination is invalid.
      const quick = this.form?.matches('.js-quick-view-form');
      const variantJSON = quick && this.form.querySelector('[data-selected-variant]');
      if (variantJSON) {
        try {
          this.showVariant(JSON.parse(variantJSON.textContent)?.id ?? '');
          return;
        } catch (_) {
          this.showVariant('');
          return;
        }
      }
      if (value !== undefined) this.showVariant(value);
    }

    syncConfigurator() {
      const target = this.querySelector('[data-guarantee-configurator-models]');
      const entries = readCatalog();
      if (!target || !entries) return;
      const roots = document.querySelectorAll('[data-drbo-configurator]');
      const sourceId = this.dataset.guaranteeConfiguratorId
        || this.closest('[data-drbo-configurator]')?.id
        || this.closest('[data-configurator-id]')?.dataset.configuratorId
        || (roots.length === 1 ? roots[0].id : '');
      if (!sourceId) return;
      const initial = window.DRBOConfiguratorState;
      const state = configuratorStates.get(sourceId) || (initial?.configuratorId === sourceId ? initial : null);
      if (!Array.isArray(state?.items)) return;
      const models = new Map();
      state.items.forEach((item) => {
        const id = String(item.id);
        if (!Object.prototype.hasOwnProperty.call(entries, id) || !Array.isArray(entries[id])) return;
        const count = readQuantity(item.quantity);
        if (count === 0) return;
        entries[id].forEach((part) => {
          if (!part || typeof part !== 'object') return;
          if (!Object.prototype.hasOwnProperty.call(families, part.model)) return;
          const unitCount = readQuantity(part.quantity);
          if (unitCount === 0) return;
          const amount = count === null || unitCount === null ? null : count * unitCount;
          const previous = models.get(part.model);
          const total = previous === null || amount === null ? null : (previous || 0) + amount;
          models.set(part.model, total !== null && !Number.isSafeInteger(total) ? null : total);
        });
      });
      const signature = JSON.stringify([...models]);
      if (this.configurationSignature === signature) return;
      const fragment = document.createDocumentFragment();
      const usedFamilies = new Set();
      let rendered = 0;
      let incomplete = false;
      for (const [model, quantity] of models) {
        const template = document.getElementById(`drbo-guarantee-model-${model}`);
        if (!(template instanceof HTMLTemplateElement)) {
          incomplete = true;
          continue;
        }
        const row = template.content.cloneNode(true);
        row.querySelectorAll('[data-guarantee-quantity]').forEach((element) => {
          element.textContent = quantity === null ? '' : `${quantity} \u00d7 `;
        });
        fragment.append(row);
        usedFamilies.add(families[model]);
        rendered += 1;
      }
      for (const family of usedFamilies) {
        const template = document.getElementById(`drbo-guarantee-family-${family}`);
        if (template instanceof HTMLTemplateElement) fragment.append(template.content.cloneNode(true));
      }
      if (modal?.open && opener && target.contains(opener)) closeDialog();
      target.replaceChildren(fragment);
      this.hidden = rendered === 0;
      this.toggleAttribute('data-guarantee-incomplete', incomplete);
      this.configurationSignature = incomplete ? undefined : signature;
    }

    showVariant(id) {
      this.querySelectorAll('[data-guarantee-variant]').forEach((section) => {
        if (section.closest(componentName) !== this) return;
        const hidden = section.dataset.guaranteeVariant !== String(id);
        if (section.hidden !== hidden) section.hidden = hidden;
        if (hidden && modal?.open && opener && section.contains(opener)) closeDialog();
      });
    }
  }

  if (!customElements.get(componentName)) customElements.define(componentName, DrboGuarantee);
})();

(() => {
  'use strict';
  if (window.garanHoverBound) return;
  window.garanHoverBound = true;
  const selector = '.nl-guarantee__label[data-nl-guarantee-dialog], .wv-guarantee__label[data-wv-guarantee-open], a.drbo-guarantee__label[data-guarantee-open], [data-garan-hover-src]';
  const hoverMedia = matchMedia('(any-hover: hover) and (any-pointer: fine)');
  const locale = document.documentElement.lang.toLowerCase();
  const closeLabel = locale.startsWith('nl') ? 'Sluiten' : locale.startsWith('de') ? 'Schlie\u00dfen' : locale.startsWith('es') ? 'Cerrar' : 'Close';
  const panel = document.createElement('div');
  panel.id = 'garan-hover-panel';
  panel.className = 'garan-hover-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', 'GARAN');
  const bar = document.createElement('div');
  bar.className = 'garan-hover-panel__bar';
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'garan-hover-panel__close';
  closeButton.setAttribute('aria-label', closeLabel);
  closeButton.textContent = '\u00d7';
  const image = document.createElement('img');
  image.className = 'garan-hover-panel__image';
  bar.append(closeButton);
  panel.append(bar, image);
  const bridge = document.createElement('div');
  bridge.className = 'garan-hover-bridge';
  bridge.hidden = true;
  bridge.setAttribute('aria-hidden', 'true');
  document.body.append(bridge, panel);
  let active = null;
  let dismissed = null;
  let timer;
  let frame;
  let lastTouch = 0;
  let oldControls;
  let returningFocus = null;
  let modalOpener = null;
  let handledEscape = false;
  const source = link => link.dataset.garanHoverSrc || link.href;
  const title = link => link.dataset.fullAlt || link.dataset.guaranteeTitle || link.dataset.title || link.getAttribute('aria-label') || 'GARAN';
  const over = node => node?.isConnected && node.matches(':hover');
  const focused = node => node?.contains(document.activeElement);

  function hide(suppress = false) {
    clearTimeout(timer);
    const restoreFocus = focused(panel);
    const previous = active;
    if (suppress) dismissed = active;
    if (active) {
      if (oldControls === null) active.removeAttribute('aria-controls');
      else active.setAttribute('aria-controls', oldControls);
    }
    panel.hidden = true;
    bridge.hidden = true;
    active = null;
    if (restoreFocus) {
      const target = previous?.isConnected && previous.getClientRects().length ? previous : document.querySelector('main');
      if (target) {
        returningFocus = previous;
        if (target.tagName === 'MAIN') target.setAttribute('tabindex', '-1');
        target.focus({preventScroll: true});
      }
    }
  }
  function position() {
    if (!active?.isConnected || !active.getClientRects().length) return hide();
    const box = active.getBoundingClientRect();
    if (box.bottom < 0 || box.top > innerHeight) {
      bridge.hidden = true;
      if (focused(panel) || focused(active)) return;
      return hide();
    }
    const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0.95;
    const width = Math.max(180, Math.min(540, innerWidth - 24, (innerHeight - 84) * ratio + 22));
    panel.style.width = width + 'px';
    const height = panel.getBoundingClientRect().height;
    let left = box.right + 10;
    if (left + width > innerWidth - 12) left = box.left - width - 10;
    if (left < 12) left = Math.min(innerWidth - width - 12, Math.max(12, box.left));
    let top = box.top;
    if (left < box.right && left + width > box.left) {
      top = box.bottom + 10;
      if (top + height > innerHeight - 12) top = box.top - height - 10;
    }
    panel.style.left = Math.max(12, left) + 'px';
    panel.style.top = Math.max(12, Math.min(top, innerHeight - height - 12)) + 'px';
    const p = panel.getBoundingClientRect();
    let b;
    if (p.right <= box.left || p.left >= box.right) {
      b = {left: Math.min(p.right, box.right) - 1, top: Math.max(p.top, box.top),
        width: Math.max(p.left - box.right, box.left - p.right) + 2,
        height: Math.max(0, Math.min(p.bottom, box.bottom) - Math.max(p.top, box.top))};
    } else {
      b = {left: Math.max(p.left, box.left), top: Math.min(p.bottom, box.bottom) - 1,
        width: Math.max(0, Math.min(p.right, box.right) - Math.max(p.left, box.left)),
        height: Math.max(p.top - box.bottom, box.top - p.bottom, 0) + 2};
    }
    Object.assign(bridge.style, {left:b.left+'px',top:b.top+'px',width:b.width+'px',height:b.height+'px'});
    bridge.hidden = b.width <= 0 || b.height <= 0;
  }
  function show(link) {
    clearTimeout(timer);
    if (dismissed === link || modalOpener === link || !link.getClientRects().length || link.closest('[hidden]') || document.querySelector('dialog[open]')) return;
    if (active === link && !panel.hidden) return;
    hide();
    active = link;
    oldControls = link.getAttribute('aria-controls');
    link.setAttribute('aria-controls', [oldControls, panel.id].filter(Boolean).join(' '));
    image.alt = title(link);
    image.src = source(link);
    panel.setAttribute('aria-label', title(link));
    panel.hidden = false;
    position();
  }
  function scheduleHide() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!over(active) && !over(panel) && !over(bridge) && !focused(active) && !focused(panel)) hide();
    }, 220);
  }
  image.addEventListener('load', () => { if (active) position(); });
  image.addEventListener('error', () => hide());
  document.addEventListener('pointerover', event => {
    if (!hoverMedia.matches || event.pointerType !== 'mouse' || Date.now() - lastTouch < 800) return;
    const link = event.target.closest?.(selector);
    if (link && !link.contains(event.relatedTarget)) show(link);
  });
  document.addEventListener('pointerout', event => {
    const link = event.target.closest?.(selector);
    if (link && !link.contains(event.relatedTarget)) {
      if (dismissed === link) dismissed = null;
      scheduleHide();
    }
  });
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'touch') { lastTouch = Date.now(); hide(); }
  }, true);
  document.addEventListener('pointermove', event => {
    if (dismissed && !dismissed.contains(event.target)) dismissed = null;
    if (modalOpener && !modalOpener.contains(event.target) && !document.querySelector('dialog[open]')) {
      modalOpener = null;
      returningFocus = null;
    }
  }, {passive: true});
  document.addEventListener('focusin', event => {
    const link = event.target.closest?.(selector);
    if (link && Date.now() - lastTouch > 800) {
      if (returningFocus === link) { returningFocus = null; dismissed = link; return; }
      if (dismissed === link) dismissed = null;
      show(link);
    }
  });
  document.addEventListener('focusout', event => {
    const link = event.target.closest?.(selector);
    if (link && dismissed === link && !over(link)) dismissed = null;
    scheduleHide();
  });
  panel.addEventListener('pointerenter', () => clearTimeout(timer));
  panel.addEventListener('pointerleave', scheduleHide);
  bridge.addEventListener('pointerenter', () => clearTimeout(timer));
  bridge.addEventListener('pointerleave', scheduleHide);
  closeButton.addEventListener('click', () => {
    const link = active;
    returningFocus = link;
    hide(true);
    link?.focus({preventScroll: true});
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Tab' && !document.querySelector('dialog[open]')) {
      modalOpener = null;
      returningFocus = null;
      dismissed = null;
    }
    if (event.key !== 'Escape' || panel.hidden) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    handledEscape = true;
    hide(true);
  }, true);
  document.addEventListener('keyup', event => {
    if (event.key === 'Escape' && handledEscape) {
      event.stopImmediatePropagation();
      handledEscape = false;
    }
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest?.(selector);
    if (link) { returningFocus = link; modalOpener = link; hide(true); }
    else if (!panel.contains(event.target)) hide();
  }, true);
  const reposition = () => {
    cancelAnimationFrame(frame);
    if (active) frame = requestAnimationFrame(position);
  };
  window.addEventListener('resize', reposition);
  document.addEventListener('scroll', reposition, {passive: true, capture: true});
  const seen = new WeakSet();
  const images = new Map();
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const url = source(entry.target);
      if (url && !images.has(url)) { const preload = new Image(); preload.src = url; images.set(url, preload); }
      observer.unobserve(entry.target);
    }
  }, {rootMargin: '240px'}) : null;
  function scan() {
    if (active && (!active.isConnected || !active.getClientRects().length)) hide();
    if (active && (image.src !== source(active) || image.alt !== title(active))) {
      image.alt = title(active);
      image.src = source(active);
      panel.setAttribute('aria-label', title(active));
      position();
    }
    document.querySelectorAll(selector).forEach(link => {
      if (seen.has(link)) return;
      seen.add(link);
      observer?.observe(link);
    });
  }
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    queueMicrotask(() => { pending = false; scan(); });
  }).observe(document.body, {childList: true, subtree: true, attributes: true,
    attributeFilter: ['hidden', 'href', 'data-full-alt', 'data-guarantee-title', 'data-title', 'data-garan-hover-src']});
  hoverMedia.addEventListener('change', () => { if (!hoverMedia.matches) hide(); });
  scan();
})();
